import { Component, OnInit } from '@angular/core';
import { Observable, forkJoin, lastValueFrom, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, RouterEvent } from '@angular/router';
import { SessionStorageService } from 'angular-web-storage';
import { ClaimsSettingsService } from './services/settings/settings.service';
import {
  SponsorService, ISponsor, UtilitiesService, LocalizationService, ReferenceService, IGetUIElementsPayload,
  IGetUIElement, IPrivacyNoticeLanguage, ICookieNoticeLanguage, ITermsOfUse, LoggingService, ConfigService,
  MsalAuthenticationService, BaseCustomerService, BaseAccountService, UserName, AssentlyService,
  SharedSettingsService, ExternalValidationResponse, ExternalValidationRequest, ExternalValidationService
} from 'wtw-ui-components';
import { TranslateService } from '@ngx-translate/core';
import cssVars from 'css-vars-ponyfill';

const okButtonLabel = 'Shared.lblOk';
const inactiveErrorPath = 'SiteInactive';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit {

  isLoading = false;
  initialized: boolean;
  okButtonText: string;
  footerLinksAndContent: object;
  applicationCurrentURL: string;
  errorTitle: string;
  errorContent: string;
  hasSponsorErrors = false;

  private sponsor: ISponsor;
  private sponsorName: string;
  private url: string = '';
  private routePrefix = '';
  private isProductDetail: boolean = false;


  constructor(
    private router: Router,
    // Todo: Let's try and avoid using SessionStorageService, instead use below ClaimsSettingsService.
    private sessionStorage: SessionStorageService,
    private settingsService: ClaimsSettingsService,
    private sponsorService: SponsorService,
    private utilitiesService: UtilitiesService,
    private localizationService: LocalizationService,
    private translate: TranslateService,
    private referenceService: ReferenceService,
    private loggingService: LoggingService,
    private configService: ConfigService,
    private readonly baseCustomerService: BaseCustomerService,
    private baseAccountService: BaseAccountService,
    // Do not remove the below unreferenced dependency as it is required to ensure that the MSAL library
    // is initialized after common auth redirection.
    private msalAuthenticationService: MsalAuthenticationService,
    private assentlyService: AssentlyService,
    private baseSettingsService: SharedSettingsService,
    private externalValidationService: ExternalValidationService
  ) {
    router.events.subscribe((routerEvent) => {
      this.checkRouterEvent(routerEvent);
    });
    sponsorService.languageObservable.subscribe(languageId => {
      this.settingsService.languageId = languageId;
      this.loadLanguageSpecificContent();
    });

  }

  checkRouterEvent(routerEvent): void {
    if (routerEvent instanceof NavigationStart) {
      // Upon initiating navigation we will always re-attempt to initialize Application Insights.
      if (this.sponsorName) {
        const instrumentationKey: string = this.configService.getServiceConfigData().appInsights.instrumentationKey;
        this.loggingService.initializeAppInsights(instrumentationKey, 'B2CClaimsV2', this.sponsorName, '');
      }
      this.isLoading = true;
    }

    if (routerEvent instanceof NavigationEnd &&
      routerEvent.urlAfterRedirects !== '/error-page') {
      this.isLoading = false;
      this.sessionStorage.set('ErrorPagePreviousUrl', routerEvent.urlAfterRedirects);
      let currentPageRoute = this.router.url.split('/').pop();
      if (currentPageRoute?.toLowerCase().indexOf('product') > -1) {
        currentPageRoute = this.sessionStorage.get('ProductName') ?? '';
        this.isProductDetail = true;
      }
      this.routePrefix = currentPageRoute.replace(/[^\w+]/gi, '-');
      this.sessionStorage.set('PageId', '');
    }

    if (routerEvent instanceof NavigationEnd ||
      routerEvent instanceof NavigationCancel ||
      routerEvent instanceof NavigationError) {
      this.settingsService.applicationPreviousURL = this.applicationCurrentURL;
      this.applicationCurrentURL = routerEvent.url;
      this.isLoading = false;
    }
  }

  getAssentlyURL() {
    this.assentlyService.getassentlyurl().then(data => {
      this.url = data?.url
      if (this.url !== '') {
        const id = document.getElementById("assentlyUrl");
        if (id === null) {
          const script = document.createElement('script');
          script.src = this.url;
          script.id = "assentlyUrl"
          document.head.appendChild(script);
        }
      }

    });
  }

  ngOnInit() {

    // Todo: inject constants service
    this.sponsorName = this.settingsService.sponsorName || this.utilitiesService.getUrlParameterByName('sname');

    // TEMP! - Defaulting Product Id to 1405 (Rejseforkring) until populated in Select Policy Page.
    // this.settingsService.productId = 1405;

    const currentUrl = this.settingsService.currentUrl;
    const previousUrl = this.settingsService.previousUrl;


    // Get 'id_token' from query string and store it local storage.
    const idToken = this.utilitiesService.getUrlParameterByName('id_token', false);
    if (idToken) {
      localStorage.setItem('msal.idtoken', idToken);
      const currentTime = new Date();
      // Add 30 minutes in current time and stores it in session storage to check expiry of generated 'id_token'.
      localStorage.setItem('idTokenExpiryTime', String(currentTime.setMinutes(currentTime.getMinutes() + 30)));
    }

    // Stash the SponsorName for later use. Skip this if we are re-routing after authentication.
    if (!currentUrl && !previousUrl) {
      // this.title.setTitle(`Home - ${this.sponsorName}`);
      this.settingsService.sponsorName = this.sponsorName;
    }

    // Pull in shared translations.
    // this language will be used as a fallback when a translation isn't found in the current language
    const currentLang = this.sessionStorage.get('LanguageCode');
    if (currentLang === null || currentLang === undefined) {
      this.translate.setDefaultLang('en-GB');
    }
    else {
      this.translate.setDefaultLang(currentLang);
    }

    this.sponsorService.getSponsor().subscribe(result => {

      // Todo: Do we need to verify site activity?
      this.sponsor = result;
      if (this.sponsor?.activateNewClaimsWebsiteUrl) {
        this.hasSponsorErrors = false;
        this.sessionStorage.set('SiteActive', !this.hasSponsorErrors);

        if (this.sponsor?.activateNewClaimsWebsiteUrl && this.sponsor?.enableAssently) {
          this.getAssentlyURL();
        }



        this.setupOnetrustCookieBanner(this.sponsor.oneTrustCookieBannerScriptId);

        // PBI-187615
        this.setLocalDateFormat();

        this.sponsorService.applyWillisBranding = this.sponsor.applyWillisBranding;
        this.sponsorService.sponsorLogo = this.sponsor.sponsorLogo;
        this.settingsService.enableSponsorLogo = !this.sponsor.applyWillisBranding;
        this.settingsService.sponsorId = this.sponsor.sponsorId;
        this.settingsService.timeZone = this.sponsor.timeZone;
        this.settingsService.timeZoneOffset = this.sponsor.timeZoneOffset;
        this.settingsService.disableNewClaims = this.sponsor.disableNewClaims;
        this.settingsService.enableGuestPurchase = this.sponsor.enableGuestPurchase;
        this.settingsService.enableAssently = this.sponsor.enableAssently;
        this.settingsService.enableCustomerIdentityB2CClaims = this.sponsor.enableCustomerIdentityB2CClaims;
        this.settingsService.allowCustomerIdentification = this.sponsor.allowCustomerIdentification;
        this.settingsService.blockedTimeForCutomerIdentifaction = this.sponsor.blockedTimeForCutomerIdentifaction;
        this.baseSettingsService.minimumCharForAutoSuggestion = this.sponsor.minimumCharactersforWebServiceCall;
        let customerId = this.sessionStorage.get('customerId');
        this.referenceService.applySponsorLogoAsFavicon(this.sponsor.sponsorId, this.sponsor.applyWillisBranding);

        if (customerId !== null && customerId !== undefined && customerId >= 0
          && this.msalAuthenticationService.isAuthenticated()) {
          this.setCustomerPreferredLanguage(customerId);
        } else if (!customerId && localStorage.hasOwnProperty('msal.idtoken') && this.msalAuthenticationService.isAuthenticated()) {
          /* After the login we couldn't see the CustomerId in Session.
              Hence, it couldn't set Customer's Preferred Language
              to handle that scenario we have added this else-if block */
          this.baseAccountService.getUserName().then((userDetail: UserName) => {
            this.settingsService.customerId = userDetail.customerID;
            customerId = userDetail.customerID;
            this.baseSettingsService.loggedInUser = userDetail.firstName;
            this.baseSettingsService.customerId = userDetail.customerID;
            this.setCustomerPreferredLanguage(customerId);
          });
        } else {
          this.setSponsorLanguage();
          this.settingsService.isPreferredLanguageSet = true;
        }


        // the lang to use, if the lang isn't available, it will use the current loader to get them
        lastValueFrom(this.translate.use(this.settingsService.languageCode));
        // Trigger asynchronously, fire and forget.
        this.loadLocalizationAndPageContent().subscribe();
        if ((localStorage.getItem('msal.error') === 'access_denied' || !previousUrl) && currentUrl) {
          // Post authentication - error.
          this.router.navigate([currentUrl]);
          this.clearPreviousRoutes();
        } else if (previousUrl) {
          // Post authentication - success.
          this.router.navigate([previousUrl]);
          this.clearPreviousRoutes();
        } else if (this.settingsService.enableAssently && this.msalAuthenticationService.isAuthenticated()
          && this.msalAuthenticationService.isSessionTimedOut()) {
          this.router.navigate(['account/confirm-registration']);
        }
        // if allow customer identification flag is enabled, then it should act like guest purchase
        else if (this.settingsService.allowCustomerIdentification && localStorage.getItem('msal.idtoken') == null) {
          this.conditionallyGetExternalValidationStatus().subscribe(async (result) => {
            if (result) {
              if (result && result?.success === true && this.baseSettingsService.externalValidationResult == null) {
                this.processExternalValidationResult(result);
              } else {
                // Initial navigation.
                this.router.initialNavigation();
              }
            }
          });
        }
        else {
          // Initial navigation.
          this.router.initialNavigation();
        }

        this.ApplyBrandingSettings();
        if (this.sponsor.activateNewClaimsWebsiteUrl) {
          this.createTermsOfUseScriptBox();
        }
      }
      else {
        this.hasSponsorErrors = true;
        this.loadErrorPageContent();
      }
      // IE fallback measure.
      cssVars({
        watch: true
      });
    });


    this.settingsService.getGlobalLoader().subscribe((loaderFlag) => {
      this.isLoading = loaderFlag;
    });

  }

  private setCustomerPreferredLanguage(customerId: number) {
    this.baseCustomerService.getCustomerPreferredLanguage(customerId).then((langObject) => {
      this.settingsService.userPreferredLanguageId = langObject?.languageID;
      if (langObject?.languageID && langObject?.languageID !== null) {
        this.baseCustomerService.setCustomerPreferredLanguage(langObject.languageID, langObject.languageCode);
        this.settingsService.isPreferredLanguageSet = true;
      }
    });
  }


  loadLocalizationAndPageContent(): Observable<void> {
    this.applyTranslations();
    const pageNames = ['Footer_Claims', 'ContactUs', 'Home', 'HowTo'];
    const getUiElementPayload: IGetUIElementsPayload = {
      sponsorEnglishName: this.sponsorName,
      PageNames: pageNames,
      languageId: this.settingsService.languageId,
      applicationName: 'Claims'
    };

    const getUIElement: Observable<IGetUIElement[]> = this.localizationService.getUIElements(getUiElementPayload);
    const getTermsOfUseContent: Observable<ITermsOfUse> = this.sponsorService.getTermsOfUsebySponsorName(this.sponsorName,
      this.settingsService.languageId?.toString());
    const getGlobalPrivacyPolicy: Observable<IPrivacyNoticeLanguage>
      = this.referenceService.getGlobalPrivacyPolicyText(this.sessionStorage.get('LanguageCode'));
    const getCookieNoticeData: Observable<ICookieNoticeLanguage>
      = this.referenceService.getCookieNoticeText(this.sessionStorage.get('LanguageCode'));


    return forkJoin([getUIElement, getTermsOfUseContent, getGlobalPrivacyPolicy, getCookieNoticeData])
      .pipe(map(results => {
        const [resUiElement, resTermsOfUse, resGlobalPrivacyPolicyText, resCookieNoticeData] = results;
        // this.localizationService.setUIElementObj(resUiElement); // set result of UI element into shared service
        this.localizationService.sharedUIElement = resUiElement;

        const privacyPolicyData = resGlobalPrivacyPolicyText;
        const cookieNoticeData = resCookieNoticeData;
        const termsAndConditionsData = resTermsOfUse;

        const copyrightsTextElement = resUiElement.find(item => item.uiElementCode === 'lblFooterCopyrightsLabel_Claims');
        const privacyPolicyLinkElement = resUiElement.find(item => item.uiElementCode === 'lblFooterPrivacyLabel_Claims');
        const cookieNoticeLinkElement = resUiElement.find(item => item.uiElementCode === 'lblFooterCookiesLabel_Claims');
        const termsAndConditionsLinkElement = resUiElement.find(item => item.uiElementCode === 'lblFooterTermsOfUseLabel_Claims');
        const contactUsTitleElement = resUiElement.find(item => item.uiElementCode === 'lblContactUsLable_Claims');
        const contactUsContentElement = resUiElement.find(item => item.uiElementCode === 'lblContactUsPage');
        const contactUsContactNumberElement = resUiElement.find(item => item.uiElementCode === 'lblContactUsNumber_Claims');
        const contactUsPageElement = resUiElement.find(item => item.uiElementCode === 'lblContactUsPageText_Claims');

        const copyrightsText = copyrightsTextElement?.uiElementValue;
        const privacyPolicyLink = privacyPolicyLinkElement?.uiElementValue;
        const cookieNoticeLink = cookieNoticeLinkElement?.uiElementValue;
        const termsAndConditionsLink = termsAndConditionsLinkElement?.uiElementValue;

        const contactUsTitle = contactUsTitleElement?.uiElementValue;
        const contactUsContent = contactUsContentElement?.uiElementValue;
        const contactUsTelephoneNumber = contactUsContactNumberElement?.uiElementValue;
        const contactUsPage = contactUsPageElement?.uiElementValue;

        const menuLinks = [
          {
            key: 'contactUs',
            label: contactUsTitle ? contactUsTitle : '',
            openTo: 'bottomsheet',
            title: contactUsTitle,
            content: contactUsContent,
            telephoneNumber: contactUsTelephoneNumber,
            enableCloseBtn: false,
            overlayPanelClass: 'fullWidthBottomSheet',
            helpText: contactUsPage
          },
          {
            key: 'privacyPolicy',
            label: privacyPolicyLink,
            openTo: 'bottomsheet',
            title: privacyPolicyLink,
            content: privacyPolicyData?.privacyNoticeDescription,
            enableCloseBtn: true,
            overlayPanelClass: 'fullWidthBottomSheet',
            buttonText: this.okButtonText
          },
          {
            key: 'termsOfUse',
            label: termsAndConditionsLink,
            openTo: 'bottomsheet',
            title: termsAndConditionsLink,
            content: termsAndConditionsData?.termsOfUseDescription,
            enableCloseBtn: true,
            overlayPanelClass: 'fullWidthBottomSheet',
            buttonText: this.okButtonText
          },
          {
            key: 'cookies',
            label: cookieNoticeLink,
            openTo: 'bottomsheet',
            title: cookieNoticeLink,
            content: cookieNoticeData?.description,
            enableCloseBtn: true,
            overlayPanelClass: 'fullWidthBottomSheet',
            buttonText: this.okButtonText
          }

        ];
        this.footerLinksAndContent = {
          key: 'footerlinks',
          copyrightsText,
          menuLinks
        };

        // Show content.
        this.initialized = true;
      }));
  }
  private setSponsorLanguage() {
    this.settingsService.languageCode = this.sponsor.languageCode;
    this.settingsService.languageId = this.sponsor.languageId;
    this.sponsorService.languageObservable.next(this.sponsor.languageId);
  }

  private applyTranslations() {
    this.translate.get([okButtonLabel]).subscribe((res) => {
      this.okButtonText = res[okButtonLabel];
    });
  }

  private setupOnetrustCookieBanner(cookieBannerScriptId: string) {
    const element = document.createElement('script');
    element.setAttribute('src', 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js');
    element.setAttribute('type', 'text/javascript');
    element.setAttribute('charset', 'UTF-8');
    element.setAttribute('data-domain-script', cookieBannerScriptId);
    document.head.appendChild(element);
  }

  private async loadLanguageSpecificContent() {
    await lastValueFrom(this.translate.use(this.settingsService.languageCode));
    await lastValueFrom(this.loadLocalizationAndPageContent());
    const currentUrl = window.location.pathname.replace('/claims', '');
    if (currentUrl.includes('new-claim')) {
      this.router.navigateByUrl(currentUrl);
    }
  }

  private clearPreviousRoutes() {
    this.settingsService.remove('currentUrl');
    this.settingsService.remove('previousUrl');
  }

  private setLocalDateFormat() {
    if (this.sponsor?.preferredDateFormatId > 0) {
      this.sponsorService.getLocalisedDateFormatById(this.sponsor.preferredDateFormatId).subscribe(result => {
        if (result) {
          if (result.localDateFormat) {
            this.sessionStorage.set('LocalisedDateFormat', result.localDateFormat);
          }
        }
      });
    } else {
      this.sessionStorage.set('LocalisedDateFormat', 'DD/MM/YYYY');
    }
  }

  createTermsOfUseScriptBox() {
    this.sponsorService.getJavaScript(this.sessionStorage.get('SponsorId')).subscribe(result => {
      if (result) {
        const element = document.createElement('script');
        element.setAttribute('class', 'terms-of-use');
        element.setAttribute('type', 'text/javascript');
        element.setAttribute('id', result.javaScriptUploadID);
        const inlineCode = document.createTextNode(result?.fileContent);
        element.appendChild(inlineCode);
        document.body.appendChild(element);
      }
    });
  }

  ApplyBrandingSettings() {
    this.sponsorService.getB2CWebsiteBrandingDetails(this.baseSettingsService.sponsorId).subscribe(result => {
      if (result) {
        if (result.isNewUIUXBrandingEnabled) {
          if (Object.entries) {
            // eslint-disable-next-line no-irregular-whitespace
            // Iterate over branding variables, setting relevant properties.       
            if (this.sponsor && this.sponsor.brandingSettings) {
              Object.entries<string>(this.sponsor.brandingSettings).forEach(
                ([key, value]) => document.documentElement.style.setProperty(`--${key}`, value));
            }
          }
        }
        else {
          const element = document.createElement('style');
          const inlineCode = document.createTextNode(result?.content);
          element.appendChild(inlineCode);
          document.head.appendChild(element);
        }
      }
    });
  }

  private conditionallyGetExternalValidationStatus(): Observable<ExternalValidationResponse> {
    if (this.settingsService.allowCustomerIdentification) {
      const request = new ExternalValidationRequest();
      request.sponsorId = this.sponsor.sponsorId;
      request.data = this.utilitiesService.getAllParameters();
      request.isGuestPurchase = true; // for customer identification flow create token like guest purchase
      request.appName = 'claimsv2';
      request.isB2BFlow = false;
      request.isSSOSAML = false;

      return this.externalValidationService.getExternalValidationStatus(request);
    }
    return of(null);
  }

  private processExternalValidationResult(externalValidationResult: ExternalValidationResponse) {
    this.baseSettingsService.tokenGenerationRedirectUrl = externalValidationResult?.tokenGenerationRedirectUrl;
    this.baseSettingsService.externalValidationResult = externalValidationResult;

    if (externalValidationResult && externalValidationResult != null && externalValidationResult?.success) {
      window.location.href = externalValidationResult.tokenGenerationRedirectUrl;
    }

  }

  public get getPageUniqueSelector(): string {
    if (!this.sponsorName) {
      return;
    }

    const pageId = this.sessionStorage.get('PageId');
    if (!pageId) {
      return ((this.isProductDetail ? "product-detail" : this.routePrefix) + " " + this.sponsorName
        + "-" + this.routePrefix)?.toLowerCase();
    }
    return ((this.isProductDetail ? "product-detail" : this.routePrefix) + " " + this.sponsorName
      + "-" + this.routePrefix + (pageId ? "-" + pageId : ''))?.toLowerCase();
  }

  private loadErrorPageContent(): void {
    const languageId = this.sessionStorage.get('LanguageId') || 0;
    this.localizationService.getErrorContent(inactiveErrorPath, languageId, this.sponsor?.sponsorId).subscribe((errorContent) => {
      this.errorTitle = errorContent.title;
      this.errorContent = errorContent.content;
      this.initialized = true;
    });
  }

}
